import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { ProductCardProps } from "@/types/product";
import type { ProductVisibility } from "@/types/drop";
import { normalizeStock } from "@/lib/commerce/stock";
import { getProductVisibility, isPreorderOpen } from "@/lib/commerce/availability";
const CURRENCY = (process.env["NEXT_PUBLIC_CURRENCY"] as string | undefined)?.toLowerCase() ?? "eur";
export { CURRENCY };

export { normalizeStock } from "@/lib/commerce/stock";

const CURRENCY_SYMBOLS: Record<string, string> = {
  eur: "\u20AC",
  usd: "$",
  gbp: "\u00A3",
  jpy: "\u00A5",
  cny: "\u00A5",
  krw: "\u20A9",
  inr: "\u20B9",
  brl: "R$",
  mxn: "$",
  cad: "$",
  aud: "$",
  chf: "CHF",
  sek: "kr",
  nok: "kr",
  dkk: "kr",
  pln: "z\u0142",
  czk: "K\u010D",
};

export function getCurrencySymbol(currency: string = CURRENCY): string {
  return CURRENCY_SYMBOLS[currency.toLowerCase()] ?? currency.toUpperCase();
}

export function formatPrice(amount: number, currency: string = CURRENCY): string {
  const symbol = getCurrencySymbol(currency);
  return `${amount.toFixed(2)} ${symbol}`;
}

export function formatDate(date: Date | string | number): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString("en-GB");
}

export function formatDateTime(date: Date | string | number): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const FALLBACK_IMAGE = "https://placehold.co/800x1000.png?text=Image+Unavailable";

export function ensureStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item));
  if (typeof value === "string" && value.trim().length > 0) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((item) => String(item));
    } catch (_e) {
      void _e;
    }
  }
  return [];
}

export interface TransformProductInput {
  name: string;
  price: string | null | undefined;
  images: unknown;
  sizes: unknown;
  id: number;
  stock?: unknown;
  is_featured?: boolean | null;
  is_enabled?: boolean | null;
  category?: string | null;
  description?: string | null;
  info?: string | null;
  care_instructions?: string | null;
  created_at?: Date | string | null;
  drop_id?: number | null;
  stripe_product_id?: string | null;
  stripe_price_id?: string | null;
  properties?: unknown;
  purchase_limit?: number | null;
  preorder_limit?: number | null;
  is_preorder?: boolean | null;
  drop?: {
    id?: number;
    name?: string;
    release_date?: Date | string | null;
    product_visibility?: ProductVisibility | null;
  } | null;
  low_stock_threshold?: number | null;
}

export interface TransformedProduct extends ProductCardProps {
  stock: number | Record<string, number>;
  createdAt?: string;
  category?: string;
  description?: string;
  info?: string;
  care_instructions?: string;
  is_featured?: boolean;
  is_enabled?: boolean;
  drop_id?: number;
  stripe_product_id?: string;
  stripe_price_id?: string;
  properties?: Record<string, unknown>;
  preorder_limit?: number | null;
  product_visibility?: ProductVisibility;
  drop?: {
    id: number;
    name: string;
    product_visibility?: ProductVisibility;
  } | null;
}

export function transformProduct(
  p: TransformProductInput,
  options: { preserveRestricted?: boolean } = {}
): TransformedProduct {
  const images = ensureStringArray(p.images);
  const sizes = ensureStringArray(p.sizes);
  const price = p.price ? parseFloat(p.price) : 0;

  const stock = normalizeStock(p.stock);
  const productVisibility =
    getProductVisibility({
      is_enabled: p.is_enabled,
      is_preorder: p.is_preorder,
      drop:
        p.drop?.release_date !== undefined
          ? { release_date: p.drop.release_date, product_visibility: p.drop.product_visibility }
          : null,
    }) ?? undefined;
  const isLocked =
    !options.preserveRestricted && (productVisibility === "teaser" || productVisibility === "none");

  return {
    name: isLocked ? "Locked product" : p.name,
    price: isLocked ? 0 : price,
    images: isLocked ? [] : images.length > 0 ? images : [FALLBACK_IMAGE],
    id: p.id,
    sizes: sizes.length > 0 ? sizes : ["ONE SIZE"],
    stock,
    purchase_limit: p.purchase_limit ?? null,
    is_preorder: isPreorderOpen({
      is_preorder: p.is_preorder,
      drop: p.drop?.release_date
        ? { release_date: p.drop.release_date, product_visibility: p.drop.product_visibility }
        : null,
    }),

    low_stock_threshold: p.low_stock_threshold ?? 3,
    createdAt:
      p.created_at instanceof Date
        ? p.created_at.toISOString()
        : ((p.created_at as string | undefined) ?? undefined),
    category: p.category ?? undefined,
    description: isLocked ? undefined : (p.description ?? undefined),
    info: isLocked ? undefined : (p.info ?? undefined),
    care_instructions: isLocked ? undefined : (p.care_instructions ?? undefined),
    is_featured: p.is_featured ?? undefined,
    is_enabled: p.is_enabled ?? undefined,
    drop_id: p.drop_id ?? undefined,
    stripe_product_id: p.stripe_product_id ?? undefined,
    stripe_price_id: p.stripe_price_id ?? undefined,
    properties: isLocked ? undefined : ((p.properties as Record<string, unknown>) ?? undefined),
    preorder_limit: isLocked ? undefined : (p.preorder_limit ?? undefined),
    product_visibility: productVisibility,
    drop: p.drop
      ? {
          id: p.drop.id!,
          name: p.drop.name!,
          product_visibility: p.drop.product_visibility ?? undefined,
        }
      : undefined,
  };
}

export function safeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

export function obfuscateCart(data: unknown): string {
  const json = JSON.stringify(data);
  return Buffer.from(json, "utf-8").toString("base64");
}

export function deobfuscateCart(obfuscated: string): unknown | null {
  try {
    const parts = obfuscated.split(".");
    const encoded = parts.length > 1 ? parts[0]! : obfuscated;
    const json = Buffer.from(encoded, "base64").toString("utf-8");
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function escapeLikeWildcards(str: string): string {
  return str.replace(/%/g, "\\%").replace(/_/g, "\\_");
}

export function safeDecodeURIComponent(str: string): string {
  try {
    return decodeURIComponent(str).replace(/\+/g, " ");
  } catch {
    return str.replace(/\+/g, " ");
  }
}

export function categorySlug(category: string): string {
  return category.toLowerCase().replace(/ & /g, "-").replace(/ /g, "-");
}
